/**
 * BOTÓN DE INICIO DE SESIÓN CON GOOGLE
 *
 * ⚠️ EXCLUSIVO DE ACCOUNTS — NO copiar este patrón para otra app.
 * Este componente llama supabase.auth.signInWithOAuth() directo desde el
 * navegador con redirectTo → {origin}/auth/callback. Funciona aquí porque
 * corre en el propio origen de Accounts (accounts.elfom.com), el único
 * agregado a la allowlist de Redirect URLs de Supabase Auth para ese flujo.
 *
 * Cualquier otra app del ecosistema (houseme, empresa, aroly, orbitra...)
 * debe usar startEmbeddedGoogleSignIn() de @shared/navigation
 * (packages/shared/src/navigation/embeddedGoogleOAuth.ts) — ese es el
 * patrón real, vía Core API, cubierto por el wildcard
 * https://api.elfom.com/auth/*\/google/callback que sí está en la allowlist.
 *
 * Copiar este componente para otra app rompe el login en silencio: Google
 * autentica bien, pero el handoff cae al Site URL por defecto del proyecto
 * Supabase (hoy theplatform360.app) en vez de volver a la app de origen.
 * Pasó exactamente eso con Orbitra — ver el fix en
 * apps/orbitra/src/modules/auth/components/GoogleSignInButton.tsx.
 *
 * Componente para autenticación OAuth con Google en Accounts.
 * Maneja el flujo SSO centralizado con redirectTo correcto.
 */

import React, { useState } from 'react';
import { supabase } from '@shared/lib/supabaseClient';
import { Button } from '@shared/ui/shadcn/ui/button';
import { Loader2 } from 'lucide-react';
import { cn } from '@shared/lib/utils';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

interface GoogleSignInButtonProps {
  returnTo?: string;
  app?: string;
  className?: string;
  onSignInStart?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Logo SVG de Google (oficial)
 */
const GoogleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17.64 9.20454C17.64 8.56636 17.5827 7.95272 17.4764 7.36363H9V10.845H13.8436C13.635 11.97 13.0009 12.9232 12.0477 13.5614V15.8195H15.2182C16.6545 14.5227 17.64 12.5454 17.64 9.20454Z"
      fill="#4285F4"
    />
    <path
      d="M9 18C11.43 18 13.467 17.1941 14.9564 15.8195L12.7859 13.5614C11.9455 14.1014 10.8255 14.4204 9 14.4204C6.65409 14.4204 4.67182 12.8373 3.96409 10.71H0.713636V13.0418C2.20227 15.9832 5.40409 18 9 18Z"
      fill="#34A853"
    />
    <path
      d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29V4.95818H0.713636C0.259091 5.89318 0 6.91136 0 8C0 9.08864 0.259091 10.1068 0.713636 11.0418L3.96409 10.71Z"
      fill="#FBBC05"
    />
    <path
      d="M9 3.57955C10.8886 3.57955 12.5114 4.23364 13.7136 5.34818L15.0859 3.97591C13.4636 2.59091 11.4259 1.81818 9 1.81818C5.40409 1.81818 2.20227 3.83409 0.713636 6.95818L3.96409 9.29C4.67182 7.16273 6.65409 5.57955 9 5.57955Z"
      fill="#EA4335"
    />
  </svg>
);

export const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  returnTo,
  app,
  className,
  onSignInStart,
  onError,
}) => {
  const [loading, setLoading] = useState(false);

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      onSignInStart?.();

      // ✅ CRÍTICO: redirectTo debe incluir app y returnTo EN LA URL para que al volver
      // de Google OAuth la URL de callback los conserve (algunos proveedores no pasan queryParams).
      // Si no están en la URL, AuthCallback recibe app=null y puede redirigir al default (ThePlatform360).
      const callbackPath = '/auth/callback';
      const params = new URLSearchParams();
      if (app) params.set('app', app);
      if (returnTo) params.set('returnTo', returnTo);
      const queryString = params.toString();
      const redirectTo = `${window.location.origin}${callbackPath}${queryString ? `?${queryString}` : ''}`;

      // Respaldo: guardar app, returnTo y clientOrigin (login local → Accounts prod) por si el callback pierde query
      try {
        const clientOrigin = new URLSearchParams(window.location.search).get('clientOrigin') || '';
        sessionStorage.setItem(
          'sso_oauth_app_return',
          JSON.stringify({ app: app ?? '', returnTo: returnTo ?? '', clientOrigin }),
        );
      } catch (_) {}

      // Query params adicionales para Supabase (por si los merge con redirect_uri)
      const queryParams: Record<string, string> = {};
      if (app) queryParams.app = app;
      if (returnTo) queryParams.returnTo = returnTo;

      // ⚠️ LIMITACIÓN DE GOOGLE OAUTH:
      // El nombre que aparece en la pantalla de consentimiento NO puede ser dinámico.
      // Se configura UNA VEZ en Supabase Dashboard > Authentication > Providers > Google
      // o en Google Cloud Console > OAuth consent screen.
      // 
      // Opciones:
      // 1. Usar un nombre genérico como "TheFomLabs" o "ThePlatform360" (RECOMENDADO)
      // 2. Configurar múltiples proyectos OAuth de Google (uno por app) - COMPLEJO
      //
      // El parámetro application_name aquí NO tendrá efecto real.
      // Se mantiene solo para referencia/documentación.

      console.log('[GoogleSignInButton] Iniciando OAuth con Google', {
        redirectTo,
        app,
        returnTo,
        queryParams,
        origin: window.location.origin,
      });

      // ✅ Llamar a signInWithOAuth con Google
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectTo,
          queryParams: queryParams,
        },
      });

      if (error) {
        console.error('[GoogleSignInButton] Error en signInWithOAuth:', error);
        onError?.(new Error(error.message || 'Error al iniciar sesión con Google'));
        setLoading(false);
        return;
      }

      // ✅ En Android/iOS nativo, abrir el navegador externo
      if (Capacitor.isNativePlatform() && data?.url) {
        try {
          await Browser.open({ url: data.url });
        } catch (browserError) {
          console.error('[GoogleSignInButton] Error abriendo navegador:', browserError);
          onError?.(new Error('No se pudo abrir el navegador para autenticación'));
          setLoading(false);
        }
      }
      // En web, el navegador redirige automáticamente
      // No necesitamos hacer nada más aquí

    } catch (err) {
      console.error('[GoogleSignInButton] Excepción:', err);
      onError?.(err instanceof Error ? err : new Error('Error desconocido al iniciar sesión con Google'));
      setLoading(false);
    }
  };

  return (
    <Button
      type="button"
      onClick={handleGoogleSignIn}
      disabled={loading}
      className={cn(
        'w-full bg-white hover:bg-gray-50 text-gray-900 border border-gray-300',
        'flex items-center justify-center gap-3',
        'transition-colors',
        className
      )}
      size="lg"
    >
      {loading ? (
        <>
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Conectando con Google...</span>
        </>
      ) : (
        <>
          <GoogleIcon className="h-5 w-5" />
          <span>Continuar con Google</span>
        </>
      )}
    </Button>
  );
};
